import { ReactNode } from "react";
import { ResourceName, ValidAny } from "@/types";

// ─── Column Type Definitions ──────────────────────────────────────────────────

export type ColumnType = "text" | "date" | "number" | "currency" | "reference" | "boolean" | "chip" | "function";

export interface ResourceColumnConfig {
  /** Field path on the record (dot notation supported). For reference columns, this is the _id_ref field. */
  source: string;
  /** Display label for the column header */
  label: string;
  /** Determines which RA field component to render */
  type: ColumnType;
  /** Whether the column is sortable (default: true) */
  sortable?: boolean;

  // ── Reference field options ──
  /** RA resource name to look up (e.g., "claimants", "clients") */
  reference?: ResourceName | string;
  /** Field to display from the referenced record (e.g., "name", "first_name") */
  referenceDisplay?: string;
  /** Optional link behavior: "show" navigates to the referenced record */
  referenceLink?: "show" | false;

  // ── Currency/Number options ──
  /** Intl.NumberFormat options for currency/number formatting */
  numberOptions?: Intl.NumberFormatOptions;

  // ── Chip/Status options ──
  /** Mapping of values to color names for chip display */
  chipColorMap?: Record<string, string>;

  // ── Function field options ──
  /** Custom render function for function-type columns */
  render?: (record: ValidAny) => ReactNode;

  // ── Layout options ──
  /** Fixed width for the column */
  width?: string | number;
  /** Text alignment */
  textAlign?: "left" | "center" | "right";
}

// ─── Standard Columns Per Resource ────────────────────────────────────────────
// These define the "default" identifying columns for each resource.
// Use `useResourceColumns` to omit, append, or override per context.

export const RESOURCE_COLUMNS: Partial<Record<ResourceName, ResourceColumnConfig[]>> = {
  // Matches StoplossClaimsList.tsx Datagrid columns
  [ResourceName.STOPLOSS_CLAIMS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    { source: "claim_status", label: "Status", type: "chip" },
    {
      source: "claimant_id_ref",
      label: "Claimant",
      type: "reference",
      reference: ResourceName.CLAIMANTS,
      referenceDisplay: "claimant_name",
      referenceLink: false,
    },
    {
      source: "client_id_ref",
      label: "Client",
      type: "reference",
      reference: ResourceName.CLIENTS,
      referenceDisplay: "client_legal_name",
      referenceLink: false,
    },
    { source: "total_paid_amount", label: "Total Paid", type: "currency" },
    { source: "created_datetime", label: "Created", type: "date" },
  ],

  // Matches ClaimItemsList.tsx Datagrid columns
  [ResourceName.CLAIM_ITEMS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    {
      source: "stoploss_claim_id_ref",
      label: "Claim",
      type: "reference",
      reference: ResourceName.STOPLOSS_CLAIMS,
      referenceDisplay: "aegle_id",
      referenceLink: false,
    },
    { source: "claim_status", label: "Status", type: "chip" },
    {
      source: "claimant_id_ref",
      label: "Claimant",
      type: "reference",
      reference: ResourceName.CLAIMANTS,
      referenceDisplay: "claimant_name",
      referenceLink: false,
    },
    {
      source: "client_id_ref",
      label: "Client",
      type: "reference",
      reference: ResourceName.CLIENTS,
      referenceDisplay: "client_legal_name",
      referenceLink: false,
    },
    { source: "paid_amount", label: "Paid Amount", type: "currency" },
    { source: "created_datetime", label: "Created", type: "date" },
  ],

  // Matches PolicyList.tsx Datagrid columns
  [ResourceName.POLICIES]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    { source: "status", label: "Status", type: "chip" },
    {
      source: "vendor_id_ref",
      label: "Carrier",
      type: "reference",
      reference: ResourceName.VENDORS,
      referenceDisplay: "company_name",
      referenceLink: false,
    },
    {
      source: "client_id_ref",
      label: "Client",
      type: "reference",
      reference: ResourceName.CLIENTS,
      referenceDisplay: "client_legal_name",
      referenceLink: false,
    },
    { source: "effective_start_date", label: "Effective Start", type: "date" },
    { source: "effective_end_date", label: "Effective End", type: "date" },
    { source: "contract_basis", label: "Contract Basis", type: "text" },
  ],

  // Matches ClaimantList.tsx — uses claimant_name + client reference
  [ResourceName.CLAIMANTS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    { source: "claimant_name", label: "Claimant", type: "text" },
    {
      source: "client_id_ref",
      label: "Client",
      type: "reference",
      reference: ResourceName.CLIENTS,
      referenceDisplay: "client_legal_name",
      referenceLink: false,
    },
  ],

  // Matches ClientList.tsx Datagrid columns (key identifying columns)
  [ResourceName.CLIENTS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    { source: "client_legal_name", label: "Client Name", type: "text" },
    { source: "client_status", label: "Status", type: "chip" },
    { source: "client_short_name", label: "Short Name", type: "text" },
    { source: "client_state_headquartered", label: "State", type: "text" },
    { source: "has_active_policy", label: "Active Policy", type: "boolean" },
  ],

  // Matches VendorsList.tsx Datagrid columns
  [ResourceName.VENDORS]: [{ source: "company_name", label: "Vendor Name", type: "text" }],

  // Matches TaskList.tsx — title + status (custom fields omitted)
  [ResourceName.TASKS]: [
    { source: "title", label: "Title", type: "text" },
    { source: "status", label: "Status", type: "chip" },
  ],

  // Documents don't use a standard Datagrid — keep identifying columns
  [ResourceName.DOCUMENTS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    { source: "file_name", label: "File Name", type: "text" },
    { source: "document_type", label: "Type", type: "text" },
  ],

  // Matches ReimbursementList.tsx Datagrid columns
  [ResourceName.REIMBURSEMENTS]: [
    { source: "aegle_id", label: "Aegle ID", type: "text" },
    {
      source: "claimant_id_ref",
      label: "Claimant",
      type: "reference",
      reference: ResourceName.CLAIMANTS,
      referenceDisplay: "claimant_name",
      referenceLink: false,
    },
    {
      source: "claim_id_ref",
      label: "Claim",
      type: "reference",
      reference: ResourceName.STOPLOSS_CLAIMS,
      referenceDisplay: "aegle_id",
      referenceLink: false,
    },
    { source: "status", label: "Status", type: "chip" },
    { source: "requested_amount", label: "Requested", type: "currency" },
    { source: "reimbursement_amount", label: "Reimbursement", type: "currency" },
    {
      source: "client_id_ref",
      label: "Client",
      type: "reference",
      reference: ResourceName.CLIENTS,
      referenceDisplay: "client_legal_name",
      referenceLink: false,
    },
    { source: "service_start_date", label: "Service Start", type: "date" },
  ],

  // Endorsements — no standalone list page, keep identifying columns
  [ResourceName.ENDORSEMENTS]: [
    { source: "endorsement_type", label: "Type", type: "chip" },
    {
      source: "policy_id_ref",
      label: "Policy",
      type: "reference",
      reference: ResourceName.POLICIES,
      referenceDisplay: "policy_number",
      referenceLink: false,
    },
  ],
};

// ─── Shared Columns ───────────────────────────────────────────────────────────
// Reusable column sets that can be appended to any resource's columns.

export const DELETED_RECORD_COLUMNS: ResourceColumnConfig[] = [
  { source: "deleted_datetime", label: "Deleted", type: "date" },
  {
    source: "deleted_by",
    label: "Deleted By",
    type: "reference",
    reference: ResourceName.USERS,
    referenceDisplay: "full_name",
    referenceLink: false,
  },
];

export const AUDIT_COLUMNS: ResourceColumnConfig[] = [
  { source: "created_by", label: "Created By", type: "text" },
  { source: "created_datetime", label: "Created", type: "date" },
  { source: "modified_by", label: "Modified By", type: "text" },
  { source: "modified_datetime", label: "Modified", type: "date" },
];
